import { useState } from 'react'
import { Download, Eye, FileText, RefreshCw, Trash2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { usePropertyWeeklyEmails } from '@/hooks/useApi'
import { api } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface WeeklyEmailRecord {
  period_start?: string | null
  period_label?: string | null
  property_id?: string | null
  user_id?: string | null
  email?: string | null
  sent_at?: string | null
  success?: boolean
  new_bookings_count?: number | null
  /** admin-api holds this send as it went out, so a PDF of it can be opened (CAP-608). */
  archived?: boolean
}

interface EmailPreviewData {
  html?: string
  period_label?: string
  new_bookings_count?: number | null
  has_metrics?: boolean
}

interface WeeklyEmailsTabProps {
  propertyId: string
  isActive: boolean
}

function formatSentAt(isoDate: string | null | undefined) {
  if (!isoDate) return 'N/A'
  try {
    return format(parseISO(isoDate), 'MMM d, yyyy')
  } catch {
    return isoDate
  }
}

export function WeeklyEmailsTab({ propertyId, isActive }: WeeklyEmailsTabProps) {
  const { toast } = useToast()
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewData, setPreviewData] = useState<EmailPreviewData | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [deletingRecordKey, setDeletingRecordKey] = useState<string | null>(null)
  const [openingPdfKey, setOpeningPdfKey] = useState<string | null>(null)
  const [pdfView, setPdfView] = useState<{ url: string; title: string; filename: string } | null>(
    null
  )

  const { data, isLoading, error, refetch } = usePropertyWeeklyEmails(propertyId, {
    enabled: !!propertyId && isActive,
  })

  const records: WeeklyEmailRecord[] = data?.records || []

  const handleDeleteRecord = async (record: WeeklyEmailRecord) => {
    const key = `${record.period_start}:${record.property_id}:${record.user_id}`
    setDeletingRecordKey(key)
    try {
      // Only invoked from the delete button below, which is itself only
      // rendered once these three fields are all present.
      await api.deleteReportEmailRecord({
        period_start: record.period_start as string,
        propertyId: record.property_id as string,
        userId: record.user_id as string,
      })
      toast({ title: 'Record removed', description: 'The email can now be re-sent.' })
      refetch()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Delete failed',
        description: message || 'Could not remove the record',
        variant: 'destructive',
      })
    } finally {
      setDeletingRecordKey(null)
    }
  }

  const handleOpenPdf = async (record: WeeklyEmailRecord) => {
    const key = `${record.period_start}:${record.property_id}:${record.user_id}`
    setOpeningPdfKey(key)
    try {
      // Only invoked from the PDF button below, which is itself only rendered
      // once these three fields are all present.
      const { blob, filename } = await api.getReportEmailPdf({
        period_start: record.period_start as string,
        propertyId: record.property_id as string,
        userId: record.user_id as string,
      })
      // Shown in a dialog, like Preview, rather than a new tab: a tab opened
      // after the fetch resolves is no longer a user gesture and popup
      // blockers drop it.
      setPdfView({
        url: URL.createObjectURL(blob),
        title: `${record.period_label || record.period_start} · ${record.email}`,
        filename: filename ?? `Capehost report - ${record.period_start}.pdf`,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'PDF unavailable',
        description: message || 'Could not load the report PDF',
        variant: 'destructive',
      })
    } finally {
      setOpeningPdfKey(null)
    }
  }

  const closePdfView = () => {
    if (pdfView) URL.revokeObjectURL(pdfView.url)
    setPdfView(null)
  }

  const handlePreview = async () => {
    setPreviewOpen(true)
    setPreviewData(null)
    setIsLoadingPreview(true)
    try {
      const result = await api.getReportEmailPreview(propertyId)
      setPreviewData(result)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : undefined
      toast({
        title: 'Preview failed',
        description: message || 'Could not load email preview',
        variant: 'destructive',
      })
      setPreviewOpen(false)
    } finally {
      setIsLoadingPreview(false)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle>Weekly Emails</CardTitle>
              <CardDescription>Weekly performance emails sent to property owners.</CardDescription>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={handlePreview}>
                  <Eye className="mr-2 h-4 w-4" />
                  Preview
                </Button>
              </TooltipTrigger>
              <TooltipContent>Preview the current-period email for this property</TooltipContent>
            </Tooltip>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <LoadingSkeleton className="h-64 w-full" />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState error={error} onRetry={refetch} />
            </div>
          ) : records.length === 0 ? (
            <div className="p-6">
              <EmptyState message="No weekly emails sent for this property yet" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Bookings</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record, i) => {
                    const recordKey = `${record.period_start}:${record.property_id}:${record.user_id}`
                    const isDeleting = deletingRecordKey === recordKey
                    const isOpeningPdf = openingPdfKey === recordKey
                    const hasIds = !!(record.period_start && record.property_id && record.user_id)
                    return (
                      <TableRow key={`${record.period_start}-${record.email}-${i}`}>
                        <TableCell className="font-medium">
                          {record.period_label || record.period_start || '—'}
                        </TableCell>
                        <TableCell>{record.email}</TableCell>
                        <TableCell>{formatSentAt(record.sent_at)}</TableCell>
                        <TableCell>
                          {record.success ? (
                            <Badge variant="success">Sent</Badge>
                          ) : (
                            <Badge variant="destructive">Failed</Badge>
                          )}
                        </TableCell>
                        <TableCell>{record.new_bookings_count ?? 0}</TableCell>
                        <TableCell className="whitespace-nowrap text-right">
                          {hasIds && record.archived && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={isOpeningPdf}
                                  onClick={() => handleOpenPdf(record)}
                                  aria-label={`View PDF of the ${record.period_label || record.period_start} report sent to ${record.email}`}
                                >
                                  {isOpeningPdf ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <FileText className="h-4 w-4" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View the PDF of this email as sent</TooltipContent>
                            </Tooltip>
                          )}
                          {hasIds && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={isDeleting}
                                  onClick={() => handleDeleteRecord(record)}
                                  aria-label={`Remove the send lock for ${record.email}`}
                                >
                                  {isDeleting ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Remove lock so email can be re-sent</TooltipContent>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={previewOpen}
        onOpenChange={open => {
          if (!open) {
            setPreviewOpen(false)
            setPreviewData(null)
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              {previewData
                ? `${previewData.period_label} · ${previewData.new_bookings_count ?? 0} new booking${previewData.new_bookings_count === 1 ? '' : 's'} · metrics ${previewData.has_metrics ? 'available' : 'unavailable'}`
                : 'Loading preview…'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto rounded border bg-white">
            {isLoadingPreview ? (
              <div className="flex items-center justify-center p-12">
                <RefreshCw className="h-6 w-6 animate-spin text-textMuted" />
              </div>
            ) : previewData?.html ? (
              <iframe
                srcDoc={previewData.html}
                title="Email preview"
                className="h-[60vh] w-full border-0"
                sandbox="allow-same-origin"
              />
            ) : (
              <p className="p-6 text-sm text-textMuted">No preview available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pdfView} onOpenChange={open => !open && closePdfView()}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4 pr-8">
              <div>
                <DialogTitle>Report as sent</DialogTitle>
                <DialogDescription>{pdfView?.title}</DialogDescription>
              </div>
              {pdfView && (
                <Button variant="outline" size="sm" asChild>
                  <a href={pdfView.url} download={pdfView.filename}>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </a>
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden rounded border bg-white">
            {pdfView && (
              <iframe src={pdfView.url} title="Report PDF" className="h-[70vh] w-full border-0" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
